<template>
  <div class="messages chat-message-bubble-row">
    <div
      v-if="isOwn"
      class="message-row message-row-own"
      role="article"
      aria-label="You"
    >
      <div class="newMsg chat-msg-text">{{ displayText }}</div>
    </div>
    <div
      v-else
      class="message-row message-row-other"
      role="article"
      aria-label="AI Assistant"
    >
      <div class="received first chat-msg-text chat-msg-markdown" v-html="renderedHtml" />
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
});

const defaultLinkOpen = md.renderer.rules.link_open
  || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  const aIndex = token.attrIndex('target');
  if (aIndex < 0) token.attrPush(['target', '_blank']);
  else token.attrs[aIndex][1] = '_blank';
  const rIndex = token.attrIndex('rel');
  if (rIndex < 0) token.attrPush(['rel', 'noopener noreferrer']);
  else token.attrs[rIndex][1] = 'noopener noreferrer';
  return defaultLinkOpen(tokens, idx, options, env, self);
};

export default {
  name: 'ChatMessageBubbleRow',
  props: {
    isOwn: {
      type: Boolean,
      required: true,
    },
    text: {
      type: String,
      default: '',
    },
  },
  computed: {
    displayText() {
      return this.text == null ? '' : String(this.text);
    },
    renderedHtml() {
      const raw = md.render(this.displayText);
      return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
    },
  },
};
</script>

<style scoped>
.chat-msg-markdown ::v-deep h1,
.chat-msg-markdown ::v-deep h2,
.chat-msg-markdown ::v-deep h3,
.chat-msg-markdown ::v-deep h4,
.chat-msg-markdown ::v-deep h5,
.chat-msg-markdown ::v-deep h6 {
  font-size: 16px;
  font-weight: 600;
  margin: 0.4em 0 0.2em;
  line-height: 1.3;
}
.messages {
  background-color: rgb(255, 255, 255);
  padding: 2px;
}

.message-row-own {
  text-align: right;
}

.message-row-other {
  text-align: left;
}

.message-row-own .newMsg {
  border-radius: 10px 0 10px 10px !important;
}

.newMsg,
.received {
  display: inline-block;
  word-break: break-word;
  font-size: 13px;
  margin: 1px 10px 1px 1px;
  text-align: left;
}

.newMsg {
  background-color: #ebe1cf;
  color: #2c2c2c;
  padding: 12px 15px;
  border-radius: 10px 10px 10px 10px;
}

.received {
  background: #ebebeb;
  padding: 9px 15px;
  border-radius: 10px 10px 10px 10px;
  margin: 0 10px 0 0;
}

.received.first {
  border-radius: 0 10px 10px 10px;
}

.chat-msg-text {
  white-space: pre-wrap;
}

.chat-msg-markdown {
  white-space: normal;
}

.chat-msg-markdown ::v-deep p {
  margin: 0 0 0.5em;
}

.chat-msg-markdown ::v-deep p:last-child {
  margin-bottom: 0;
}

.chat-msg-markdown ::v-deep ul,
.chat-msg-markdown ::v-deep ol {
  margin: 0.25em 0 0.5em;
  padding-left: 1.25em;
}

.chat-msg-markdown ::v-deep code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 12px;
}

.chat-msg-markdown ::v-deep pre {
  background: rgba(0, 0, 0, 0.06);
  padding: 8px 10px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0.5em 0;
}

.chat-msg-markdown ::v-deep pre code {
  background: transparent;
  padding: 0;
}

.chat-msg-markdown ::v-deep a {
  color: #bd9d5f;
  text-decoration: underline;
  word-break: break-word;
}

.chat-msg-markdown ::v-deep blockquote {
  border-left: 3px solid #bd9d5f;
  margin: 0.25em 0;
  padding-left: 8px;
  color: #555;
}

.chat-msg-markdown ::v-deep table {
  border-collapse: collapse;
  margin: 0.25em 0;
  font-size: 12px;
}

.chat-msg-markdown ::v-deep th,
.chat-msg-markdown ::v-deep td {
  border: 1px solid #d4d4d4;
  padding: 4px 8px;
}

.chat-msg-markdown ::v-deep img {
  max-height: 60px;
  width: auto;
  display: inline-block;
}
</style>
